(() => {
  'use strict';

  const RULE_VERSION = 'siwa-finder-1.0.0';
  const STEP_KEYS = ['presence', 'occasion', 'weather', 'projection', 'sweetness', 'familiar', 'budget'];
  const READ_LINES = [
    'Presence first.',
    'Place matters.',
    'Read the weather.',
    'Set the volume.',
    'Balance the sweetness.',
    'Use what you know.',
    'Check live stock.',
  ];

  const PRESENCE_PROFILES = {
    composed: {
      label: 'composed',
      family: /woody|amber|iris/i,
      character: /composed|polished|refined|confidence|authority|elegan|controlled/i,
      notes: /sandalwood|iris|amber|labdanum|woods?|vetiver|cedar/i,
    },
    magnetic: {
      label: 'magnetic',
      family: /warm spicy|warm vanilla|warm floral|amber/i,
      character: /magnetic|charismatic|sensual|sexy|irresistible|social|romance/i,
      notes: /cardamom|cinnamon|vanilla|amber|tonka|lavender|tobacco/i,
    },
    clean: {
      label: 'clean',
      family: /fresh|aromatic|citrus/i,
      character: /clean|fresh|sharp|prepared|refined|calm/i,
      notes: /citrus|bergamot|lemon|grapefruit|mint|musk|ambroxan|ginger|aromatic|neroli/i,
    },
    dark: {
      label: 'dark',
      family: /oud|woody|warm spicy/i,
      character: /dark|deep|smok|intense|private|night|bold/i,
      notes: /leather|oud|smok|tobacco|patchouli|rum|cognac|liquor|labdanum|resin/i,
    },
    open: {
      label: 'open',
      family: /marine|fresh|aquatic|fruity/i,
      character: /open|light|air|summer|fresh|aquatic|cooling/i,
      notes: /sea|marine|aquatic|water|ozonic|fig|citrus|bergamot|pineapple|mango|mint|grapefruit/i,
    },
    indulgent: {
      label: 'indulgent',
      family: /gourmand|sweet|vanilla/i,
      character: /rich|indulgent|gourmand|sweet|edible|addictive|cozy|butter|luxury/i,
      notes: /vanilla|praline|caramel|sugar|chocolate|coffee|cacao|honey|marshmallow|tonka|boozy|rum|cognac|pistachio|hazelnut/i,
    },
    expressive: {
      label: 'expressive',
      family: /floral|fruity|rose/i,
      character: /expressive|elegant|gorgeous|floral|fruity|beautiful|polished|magnetic/i,
      notes: /rose|peony|jasmine|violet|iris|hibiscus|orange blossom|lychee|litchi|pear|berries|musk/i,
    },
    unexpected: {
      label: 'unexpected',
      family: /original|experimental/i,
      character: /unexpected|original|experimental|distinct/i,
      notes: /$a/,
    },
  };

  const OCCASION_PATTERNS = {
    daily: /daily|all day|daytime|all year use|signature|casual|fresh starts/i,
    work: /work|business|meeting|office|smart-casual/i,
    date: /date|dinner|restaurant|romance/i,
    formal: /formal|special occasion|dressed-up|elegance/i,
    night: /night|evening/i,
    travel: /travel|weekend|beach|holiday|trip|sunny days/i,
  };

  const WEATHER_PATTERNS = {
    heat: /summer|warm weather|high heat|hot weather|beach|sunny/i,
    mild: /all year|spring|mild/i,
    cold: /winter|fall|autumn|cool|cold/i,
  };

  const DIRECTION_PROFILES = {
    fresh: {
      label: 'fresh and aquatic',
      pattern: /fresh|citrus|bergamot|lemon|orange|mandarin|grapefruit|citron|cedrat|neroli|mint|marine|sea|aquatic|watery|ozonic|salty|ginger|clean|ambroxan|ambrette/i,
    },
    fruity: {
      label: 'fruity',
      pattern: /fruit|apple|pear|lychee|litchi|berry|berries|black ?currant|blackcurrant|raspberry|pineapple|mango|apricot|fig/i,
    },
    floral: {
      label: 'floral and powdery',
      pattern: /floral|rose|peony|jasmine|lavender|iris|violet|hibiscus|orchid|blossom|powder/i,
    },
    gourmand: {
      label: 'sweet and gourmand',
      pattern: /sweet|gourmand|vanilla|sugar|caramel|praline|marshmallow|honey|chocolate|cacao|coffee|toffee|cream|croissant|butter|pistachio|hazelnut|tonka|coconut|cotton candy|amaretto|coumarin/i,
    },
    woods: {
      label: 'woods and amber',
      pattern: /woody|woods?|sandalwood|cedar|amber|benzoin|labdanum|resin|vetiver|patchouli|palo santo|moss|cashmere|musk/i,
    },
    dark: {
      label: 'spice, oud and smoke',
      pattern: /spice|cinnamon|cardamom|pepper|saffron|tobacco|leather|suede|oud|rum|cognac|liquor|boozy|smok/i,
    },
  };

  const SWEET_FAMILY_PATTERN = /sweet|gourmand|vanilla/i;
  const SWEET_CHARACTER_PATTERN = /sweet|gourmand|edible|sugar|dessert/i;
  const SWEET_NOTE_PATTERN = /vanilla|tonka|praline|caramel|marshmallow|honey|chocolate|cacao|sugar|toffee|cotton candy|ice cream|coumarin|amber|benzoin|coconut|dates|liquor|rum|cognac/i;

  const normalizeText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const textValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
    return '';
  };

  const arrayValue = (value) => {
    if (Array.isArray(value)) return value.map(textValue).filter(Boolean);
    const string = textValue(value);
    if (!string) return [];
    if (string.startsWith('[')) {
      try {
        const parsed = JSON.parse(string);
        if (Array.isArray(parsed)) return parsed.map(textValue).filter(Boolean);
      } catch (_error) {
        // Fall through to conservative plain-text splitting.
      }
    }
    return string.split(/[,;]/).map((part) => part.trim()).filter(Boolean);
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  }[character]));

  const sentence = (value) => {
    const clean = textValue(value).replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    return /[.!?]$/.test(clean) ? clean : `${clean}.`;
  };

  const joinNatural = (values) => {
    const clean = values.filter(Boolean);
    if (clean.length < 2) return clean[0] || '';
    if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
    return `${clean.slice(0, -1).join(', ')}, and ${clean.at(-1)}`;
  };

  const firstMatches = (notes, pattern, limit = 2) => arrayValue(notes).filter((note) => pattern.test(note)).slice(0, limit);

  const sizeFromText = (value) => {
    const match = textValue(value).match(/(?:^|\D)(\d{2,3})\s*(?:ml|millilitre|milliliter)/i);
    return match ? Number(match[1]) : null;
  };

  const variantSize = (variant, productSizes) => {
    const variantLabels = [variant.title, ...arrayValue(variant.options)].map(textValue).filter(Boolean);
    const direct = sizeFromText(variantLabels.join(' '));
    if (direct) return direct;
    const parsedProductSizes = arrayValue(productSizes).map(sizeFromText).filter(Number.isFinite);
    const exactNumericLabel = variantLabels.find((label) => /^\d{2,3}$/.test(label));
    const exactNumericSize = exactNumericLabel ? Number(exactNumericLabel) : null;
    if (Number.isFinite(exactNumericSize) && parsedProductSizes.includes(exactNumericSize)) return exactNumericSize;
    return parsedProductSizes.length === 1 ? parsedProductSizes[0] : null;
  };

  const projectionTier = (product) => {
    const value = normalizeText(`${product.projection} ${product.sillage}`);
    if (/intimate|close/.test(value)) return 0;
    if (/moderate|present|noticeable/.test(value)) return 1;
    if (/strong|enormous|room filling/.test(value)) return 2;
    return null;
  };

  const inferredSweetness = (product) => {
    if (SWEET_FAMILY_PATTERN.test(product.family) || SWEET_CHARACTER_PATTERN.test(product.character)) return 2;
    const notes = [...product.topNotes, ...product.heartNotes, ...product.baseNotes].join(' ');
    if (SWEET_NOTE_PATTERN.test(notes)) return 1;
    return 0;
  };

  const prepareProduct = (rawProduct) => {
    const product = {
      ...rawProduct,
      title: textValue(rawProduct.title),
      handle: textValue(rawProduct.handle),
      url: textValue(rawProduct.url),
      image: textValue(rawProduct.image),
      imageAlt: textValue(rawProduct.imageAlt) || textValue(rawProduct.title),
      family: textValue(rawProduct.family),
      character: textValue(rawProduct.character),
      topNotes: arrayValue(rawProduct.topNotes),
      heartNotes: arrayValue(rawProduct.heartNotes),
      baseNotes: arrayValue(rawProduct.baseNotes),
      occasions: arrayValue(rawProduct.occasions),
      weather: arrayValue(rawProduct.weather),
      season: textValue(rawProduct.season),
      intensity: textValue(rawProduct.intensity),
      sillage: textValue(rawProduct.sillage),
      projection: textValue(rawProduct.projection),
      audience: textValue(rawProduct.audience),
      referenceHouse: textValue(rawProduct.referenceHouse),
      referenceName: textValue(rawProduct.referenceName),
      original: rawProduct.original === true,
      productLine: textValue(rawProduct.productLine),
      sizes: arrayValue(rawProduct.sizes),
    };

    product.variants = Array.isArray(rawProduct.variants)
      ? rawProduct.variants.map((variant) => ({
        id: String(variant.id ?? ''),
        title: textValue(variant.title),
        options: arrayValue(variant.options),
        price: Number(variant.price),
        available: variant.available === true,
        size: variantSize(variant, product.sizes),
        image: textValue(variant.image) || product.image,
        imageAlt: textValue(variant.imageAlt) || product.imageAlt,
      })).filter((variant) => variant.id && Number.isFinite(variant.price))
      : [];

    product.availableVariants = product.variants.filter((variant) => variant.available);
    product.projectionTier = projectionTier(product);
    product.sweetnessTier = inferredSweetness(product);
    product.occasionText = product.occasions.join(' ');
    product.weatherText = [...product.weather, product.season, ...product.occasions].join(' ');
    return product;
  };

  const eligibleVariants = (product, sizeChoice, budgetEgp) => {
    const wantedSize = sizeChoice === 'body' ? null : Number(sizeChoice);
    let variants = product.availableVariants.filter((variant) => {
      if (sizeChoice === 'body') return variant.size === 120 || variant.size === 125;
      return variant.size === wantedSize;
    });

    if (budgetEgp > 0) {
      const maximumPrice = budgetEgp * 100;
      variants = variants.filter((variant) => variant.price <= maximumPrice);
    }

    return variants.sort((left, right) => left.price - right.price || left.title.localeCompare(right.title));
  };

  const findVariant = (product, sizeChoice, budgetEgp) => eligibleVariants(product, sizeChoice, budgetEgp)[0] || null;

  const presenceMatch = (product, selected) => {
    const profile = PRESENCE_PROFILES[selected];
    if (!profile) return { points: 0, evidence: [], direct: 0 };
    if (selected === 'unexpected') {
      if (product.original) return { points: 10, evidence: ['it is listed as a Siwa Original'], direct: 1 };
      const characterMatch = profile.character.test(product.character);
      return {
        points: characterMatch ? 3 : 0,
        evidence: characterMatch ? ['its stated character supports a less expected direction'] : [],
        direct: characterMatch ? 1 : 0,
      };
    }

    let points = 0;
    let direct = 0;
    const evidence = [];
    if (profile.family.test(product.family)) {
      points += 4;
      direct += 1;
      evidence.push(`its ${product.family} family supports a ${profile.label} direction`);
    }
    if (profile.character.test(product.character)) {
      points += 3;
      direct += 1;
      evidence.push('its stated character matches the presence you chose');
    }
    const topMatches = firstMatches(product.topNotes, profile.notes);
    const heartMatches = firstMatches(product.heartNotes, profile.notes);
    const baseMatches = firstMatches(product.baseNotes, profile.notes);
    if (topMatches.length) {
      points += 2;
      direct += 1;
      evidence.push(`${joinNatural(topMatches)} appear in the opening`);
    }
    if (heartMatches.length) {
      points += 1;
      direct += 1;
      evidence.push(`${joinNatural(heartMatches)} shape the heart`);
    }
    if (baseMatches.length) {
      points += 1;
      direct += 1;
      evidence.push(`${joinNatural(baseMatches)} remain in the base`);
    }
    return { points: Math.min(10, points), evidence, direct };
  };

  const occasionMatch = (product, selected) => {
    const pattern = OCCASION_PATTERNS[selected];
    if (!pattern || !product.occasionText) return { points: 0, evidence: [], direct: 0 };
    const matched = product.occasions.find((occasion) => pattern.test(occasion));
    return matched
      ? { points: 6, evidence: [`it is listed for ${matched.toLowerCase()}`], direct: 1 }
      : { points: 0, evidence: [], direct: 0 };
  };

  const weatherMatch = (product, selected) => {
    const pattern = WEATHER_PATTERNS[selected];
    if (!pattern) return { points: 0, evidence: [], direct: 0 };
    const explicitValues = [...product.weather, product.season, ...product.occasions];
    const matched = explicitValues.find((value) => pattern.test(value));
    if (matched) return { points: 5, evidence: [`its stored season guidance includes ${matched.toLowerCase()}`], direct: 1 };
    if (selected !== 'mild' && /all year/i.test(product.weatherText)) {
      return { points: 1, evidence: ['it is listed for all-year wear'], direct: 1 };
    }
    return { points: 0, evidence: [], direct: 0 };
  };

  const projectionMatch = (product, selected) => {
    const requested = { close: 0, noticeable: 1, strong: 2 }[selected];
    if (!Number.isInteger(requested) || !Number.isInteger(product.projectionTier)) {
      return { points: 0, evidence: [], direct: 0 };
    }
    const distance = Math.abs(requested - product.projectionTier);
    const points = distance === 0 ? 4 : distance === 1 ? 2 : 0;
    const stored = product.sillage || product.projection;
    return {
      points,
      evidence: points > 0 && stored ? [`its stored sillage is ${stored.toLowerCase()}`] : [],
      direct: points > 0 ? 1 : 0,
    };
  };

  const sweetnessMatch = (product, selected) => {
    const requested = { dry: 0, balanced: 1, sweet: 2 }[selected];
    if (!Number.isInteger(requested)) return { points: 0, evidence: [], direct: 0 };
    const distance = Math.abs(requested - product.sweetnessTier);
    const points = distance === 0 ? 4 : distance === 1 ? 2 : 0;
    const noteMatches = firstMatches([...product.topNotes, ...product.heartNotes, ...product.baseNotes], SWEET_NOTE_PATTERN, 2);
    const evidence = [];
    if (points && noteMatches.length) evidence.push(`${joinNatural(noteMatches)} informed the sweetness ranking`);
    else if (points && SWEET_FAMILY_PATTERN.test(product.family)) evidence.push(`its ${product.family} family informed the sweetness ranking`);
    return { points, evidence, direct: 0 };
  };

  const referenceMatch = (product, query) => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return { points: 0, evidence: [], direct: 0 };
    const referenceName = normalizeText(product.referenceName);
    const referenceHouse = normalizeText(product.referenceHouse);
    const exactName = referenceName && (normalizedQuery === referenceName || normalizedQuery.includes(referenceName));
    const exactHouse = referenceHouse && (normalizedQuery === referenceHouse || normalizedQuery.includes(referenceHouse));
    const partialName = normalizedQuery.length >= 3 && referenceName.includes(normalizedQuery);
    const partialHouse = normalizedQuery.length >= 3 && referenceHouse.includes(normalizedQuery);
    if (exactName) {
      return {
        points: 8,
        evidence: [`its stored reference is ${product.referenceName}${product.referenceHouse ? ` by ${product.referenceHouse}` : ''}`],
        direct: 1,
      };
    }
    if (exactHouse || partialHouse) {
      return {
        points: 4,
        evidence: [`its stored reference house is ${product.referenceHouse}`],
        direct: 1,
      };
    }
    if (partialName) {
      return {
        points: 6,
        evidence: [`its stored reference is ${product.referenceName}`],
        direct: 1,
      };
    }
    return { points: 0, evidence: [], direct: 0 };
  };

  const directionMatch = (product, selected) => {
    const profile = DIRECTION_PROFILES[selected];
    if (!profile) return { points: 0, evidence: [], direct: 0 };
    let points = 0;
    let direct = 0;
    const evidence = [];
    if (profile.pattern.test(product.family)) {
      points += 4;
      direct += 1;
      evidence.push(`its stated family is ${product.family}`);
    }
    if (profile.pattern.test(product.character)) {
      points += 2;
      direct += 1;
    }
    const top = firstMatches(product.topNotes, profile.pattern);
    const heart = firstMatches(product.heartNotes, profile.pattern);
    const base = firstMatches(product.baseNotes, profile.pattern);
    if (top.length) {
      points += 3;
      direct += 1;
      evidence.push(`${joinNatural(top)} lead the opening`);
    }
    if (heart.length) {
      points += 2;
      direct += 1;
      evidence.push(`${joinNatural(heart)} appear in the heart`);
    }
    if (base.length) {
      points += 1;
      direct += 1;
      evidence.push(`${joinNatural(base)} remain in the base`);
    }
    return { points: Math.min(6, points), evidence, direct };
  };

  const hasMeaningfulAnswers = (answers) => Boolean(
    answers.presence
    || answers.occasion
    || answers.weather
    || answers.projection
    || answers.sweetness
    || textValue(answers.reference)
    || answers.direction
  );

  const scoreProduct = (product, answers) => {
    const matchingVariants = eligibleVariants(product, answers.size, answers.budget);
    const selectedVariant = matchingVariants[0] || null;
    if (!selectedVariant) return null;

    let points = 0;
    let maximum = 0;
    let directMatches = 0;
    const evidence = [];
    const include = (result, maxPoints) => {
      maximum += maxPoints;
      points += result.points;
      directMatches += result.direct;
      evidence.push(...result.evidence);
    };

    if (answers.presence) include(presenceMatch(product, answers.presence), 10);
    if (answers.occasion) include(occasionMatch(product, answers.occasion), 6);
    if (answers.weather) include(weatherMatch(product, answers.weather), 5);
    if (answers.projection) include(projectionMatch(product, answers.projection), 4);
    if (answers.sweetness) include(sweetnessMatch(product, answers.sweetness), 4);
    if (answers.reference) include(referenceMatch(product, answers.reference), 8);
    else if (answers.direction) include(directionMatch(product, answers.direction), 6);

    const normalizedScore = maximum > 0 ? Math.max(0, Math.round((points / maximum) * 100)) : 0;
    return {
      product,
      selectedVariant,
      eligibleVariants: matchingVariants,
      points,
      maximum,
      normalizedScore,
      directMatches,
      evidence,
    };
  };

  const isBroadWear = (product) => /all year/i.test(product.weatherText);

  const dominantDirection = (product) => Object.entries(DIRECTION_PROFILES)
    .map(([key]) => ({ key, points: directionMatch(product, key).points }))
    .sort((left, right) => right.points - left.points || left.key.localeCompare(right.key))[0]?.key || '';

  const rankProducts = (products, answers) => {
    const ranked = products
      .map((product) => scoreProduct(product, answers))
      .filter(Boolean)
      .sort((left, right) => (
        right.normalizedScore - left.normalizedScore
        || right.directMatches - left.directMatches
        || right.points - left.points
        || left.product.handle.localeCompare(right.product.handle)
    ));

    if (!ranked.length) return [];
    if (!hasMeaningfulAnswers(answers)) {
      return ranked.slice(0, 3).map((candidate) => ({
        ...candidate,
        fitLabel: 'Starting point',
        neutral: true,
      }));
    }
    const selected = [];
    const best = ranked[0];
    selected.push({ ...best, fitLabel: 'Best fit' });

    const remaining = ranked.slice(1);
    const saferBandFloor = Math.max(0, (remaining[0]?.normalizedScore || 0) - 8);
    const safer = remaining.find((candidate) => (
      candidate.normalizedScore >= saferBandFloor
      && isBroadWear(candidate.product)
      && (!answers.projection || candidate.product.projectionTier <= ({ close: 0, noticeable: 1, strong: 2 }[answers.projection] ?? 2))
    )) || remaining[0];
    if (safer) selected.push({ ...safer, fitLabel: 'Safer fit' });

    const usedHandles = new Set(selected.map((candidate) => candidate.product.handle));
    const bestDirection = dominantDirection(best.product);
    const minimumUnexpectedScore = Math.max(0, best.normalizedScore - 15);
    const lessExpected = ranked.find((candidate) => (
      !usedHandles.has(candidate.product.handle)
      && candidate.normalizedScore >= minimumUnexpectedScore
      && (candidate.product.original !== best.product.original || dominantDirection(candidate.product) !== bestDirection)
    )) || ranked.find((candidate) => !usedHandles.has(candidate.product.handle));
    if (lessExpected) selected.push({ ...lessExpected, fitLabel: 'Less expected' });

    return selected.slice(0, 3);
  };

  const expectedCopy = (product) => {
    const parts = [];
    if (product.topNotes.length) parts.push(`${joinNatural(product.topNotes.slice(0, 2))} up top`);
    if (product.heartNotes.length) parts.push(`${joinNatural(product.heartNotes.slice(0, 2))} through the heart`);
    if (product.baseNotes.length) parts.push(`${joinNatural(product.baseNotes.slice(0, 2))} in the drydown`);
    if (parts.length) return sentence(parts.join('; '));
    return sentence(product.character || product.family || 'See the product page for the full build');
  };

  const worthKnowingCopy = (product) => {
    if (product.original) return 'Listed as a Siwa Original rather than an inspired reference.';
    if (product.referenceName) {
      return sentence(`Inspired by ${product.referenceName}${product.referenceHouse ? ` by ${product.referenceHouse}` : ''}`);
    }
    const facts = [product.intensity && `${product.intensity} intensity`, product.sillage && `${product.sillage} sillage`].filter(Boolean);
    return facts.length ? sentence(`Stored as ${joinNatural(facts)}`) : 'See the scent page for the complete product facts.';
  };

  const whyCopy = (candidate) => {
    if (candidate.neutral) {
      return `No scent preference was selected; this ${candidate.selectedVariant.title} variant is an available starting point.`;
    }
    const reasons = [...new Set(candidate.evidence)].slice(0, 2);
    if (reasons.length) return sentence(joinNatural(reasons));
    return `Its selected ${candidate.selectedVariant.title} variant is available within your ceiling.`;
  };

  const formatMoney = (cents, currency) => {
    const amount = Number(cents) / 100;
    if (currency === 'EGP') return `LE ${new Intl.NumberFormat('en-EG', { maximumFractionDigits: 2 }).format(amount)}`;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  };

  const resultCardMarkup = (candidate, index, currency) => {
    const { product, selectedVariant } = candidate;
    const matchingVariants = candidate.eligibleVariants || [selectedVariant];
    const selectId = `SiwaFinderVariant-${escapeHtml(product.handle)}-${index}`;
    const optionMarkup = matchingVariants.map((variant) => (
      `<option value="${escapeHtml(variant.id)}" data-price="${variant.price}" data-image="${escapeHtml(variant.image)}" data-image-alt="${escapeHtml(variant.imageAlt)}"${variant.id === selectedVariant.id ? ' selected' : ''}>${escapeHtml(variant.title)} — ${escapeHtml(formatMoney(variant.price, currency))}</option>`
    )).join('');
    const selectedImage = selectedVariant.image || product.image;
    const selectedImageAlt = selectedVariant.imageAlt || product.imageAlt;
    const imageMarkup = selectedImage
      ? `<img src="${escapeHtml(selectedImage)}" alt="${escapeHtml(selectedImageAlt)}" width="900" height="1125" loading="${index === 0 ? 'eager' : 'lazy'}" data-finder-result-image>`
      : '<span data-finder-image-placeholder>Image coming soon</span>';
    const imageClass = selectedImage ? '' : ' siwa-finder__result-media--empty';
    const kicker = [product.family, product.audience].filter(Boolean).join(' / ');
    const scoreCopy = candidate.neutral ? 'Starting point' : `${candidate.normalizedScore}%`;
    const edgeClass = index % 2 === 0 ? 'siwa-paper--edge-b' : '';

    return `
      <article class="siwa-finder__result-card siwa-paper siwa-paper--review ${edgeClass}" data-finder-result-card data-product-handle="${escapeHtml(product.handle)}">
        <a class="siwa-finder__result-media${imageClass}" href="${escapeHtml(product.url)}">
          ${imageMarkup}
          <p class="siwa-finder__fit">${escapeHtml(candidate.fitLabel)}</p>
        </a>
        <div class="siwa-finder__result-body">
          <p class="siwa-finder__result-kicker">${escapeHtml(kicker || product.productLine || 'Siwa Fragrances')}</p>
          <div class="siwa-finder__result-heading">
            <h3><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3>
            <span class="siwa-finder__result-score">${escapeHtml(scoreCopy)}</span>
          </div>
          <dl class="siwa-finder__result-facts">
            <div class="siwa-finder__result-fact"><dt>Why it fits</dt><dd>${escapeHtml(whyCopy(candidate))}</dd></div>
            <div class="siwa-finder__result-fact"><dt>Scent journey</dt><dd>${escapeHtml(expectedCopy(product))}</dd></div>
            <div class="siwa-finder__result-fact"><dt>Identity</dt><dd>${escapeHtml(worthKnowingCopy(product))}</dd></div>
          </dl>
          <div class="siwa-finder__result-buy">
            <div>
              <label class="siwa-finder__result-variant-label" for="${selectId}">Choose size</label>
              <p class="siwa-finder__result-price" data-finder-result-price>${escapeHtml(formatMoney(selectedVariant.price, currency))}</p>
            </div>
          </div>
          <select class="siwa-finder__result-select" id="${selectId}" data-finder-result-variant aria-label="Choose an eligible variant for ${escapeHtml(product.title)}">
            ${optionMarkup}
          </select>
          <div class="siwa-finder__result-actions">
            <a class="siwa-finder__result-link siwa-paper siwa-paper--interactive siwa-paper--control" href="${escapeHtml(product.url)}"><span class="siwa-paper__label">View details</span></a>
            <button class="siwa-finder__result-add siwa-paper siwa-paper--interactive siwa-paper--dark siwa-paper--edge-b" type="button" data-finder-add data-siwa-paper-wet-button><span class="siwa-paper__label">Add to bag</span></button>
          </div>
          <p class="siwa-finder__result-status" role="status" aria-live="polite" data-finder-add-status></p>
        </div>
      </article>`;
  };

  const updateCartCount = async () => {
    const rootPath = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('.siwa-header__count').forEach((count) => {
      count.textContent = cart.item_count;
    });
    document.querySelectorAll('.siwa-header__bag').forEach((bag) => {
      bag.setAttribute('aria-label', `Bag, ${cart.item_count} items`);
    });
    document.dispatchEvent(new CustomEvent('siwa:cart:updated', { detail: { cart } }));
  };

  const initFinder = (root) => {
    if (!root || root.dataset.finderInitialized === 'true') return;
    root.dataset.finderInitialized = 'true';

    const catalogNode = root.querySelector('[data-finder-catalog]');
    let catalog = { currency: root.dataset.currency || 'EGP', products: [] };
    try {
      catalog = JSON.parse(catalogNode?.textContent || '{}');
    } catch (_error) {
      catalog = { currency: root.dataset.currency || 'EGP', products: [] };
    }
    const products = Array.isArray(catalog.products) ? catalog.products.map(prepareProduct).filter((product) => product.availableVariants.length) : [];
    const currency = textValue(catalog.currency) || root.dataset.currency || 'EGP';

    const form = root.querySelector('[data-finder-form]');
    const stepsContainer = root.querySelector('[data-finder-steps]');
    const steps = [...root.querySelectorAll('[data-finder-step]')];
    const controls = root.querySelector('[data-finder-controls]');
    const backButton = root.querySelector('[data-finder-back]');
    const skipButton = root.querySelector('[data-finder-skip]');
    const nextButton = root.querySelector('[data-finder-next]');
    const nextLabel = root.querySelector('[data-finder-next-label]');
    const progress = root.querySelector('[data-finder-progress]');
    const progressBar = root.querySelector('[data-finder-progress-bar]');
    const progressLabel = root.querySelector('[data-finder-progress-label]');
    const progressValue = root.querySelector('[data-finder-progress-value]');
    const statusbar = root.querySelector('.siwa-finder__statusbar');
    const live = root.querySelector('[data-finder-live]');
    const editorialIndex = root.querySelector('[data-finder-editorial-index]');
    const read = root.querySelector('[data-finder-read]');
    const editorialRestart = root.querySelector('.siwa-finder__editorial [data-finder-restart]');
    const results = root.querySelector('[data-finder-results]');
    const resultGrid = root.querySelector('[data-finder-result-grid]');
    const resultsSummary = root.querySelector('[data-finder-results-summary]');
    const empty = root.querySelector('[data-finder-empty]');
    const catalogUnavailable = root.querySelector('[data-finder-unavailable]');
    const referenceInput = root.querySelector('[data-finder-reference]');
    const referenceList = root.querySelector('[data-finder-reference-list]');
    let currentStep = 0;

    const sizeInputs = [...form.querySelectorAll('input[name="size"]')];
    const availableSizeInputs = sizeInputs.filter((input) => (
      products.some((product) => findVariant(product, input.value, 0))
    ));
    sizeInputs.forEach((input) => {
      const hasLiveCandidate = availableSizeInputs.includes(input);
      input.disabled = !hasLiveCandidate;
      input.closest('.siwa-finder__choice')?.toggleAttribute('hidden', !hasLiveCandidate);
    });

    if (!products.length || !availableSizeInputs.length) {
      form.hidden = true;
      statusbar.hidden = true;
      results.hidden = true;
      if (catalogUnavailable) catalogUnavailable.hidden = false;
      if (editorialIndex) editorialIndex.textContent = '00';
      if (read) read.textContent = 'Verified catalog unavailable.';
      if (live) live.textContent = 'The verified Siwa finder catalog is currently unavailable.';
      return;
    }

    if (catalogUnavailable) catalogUnavailable.hidden = true;
    const defaultSize = availableSizeInputs.some((input) => input.value === '50')
      ? '50'
      : availableSizeInputs[0].value;
    sizeInputs.forEach((input) => { input.checked = input.value === defaultSize; });

    const answers = {
      presence: null,
      occasion: null,
      weather: null,
      projection: null,
      sweetness: null,
      reference: '',
      direction: null,
      size: defaultSize,
      budget: 0,
    };

    const references = new Set();
    products.forEach((product) => {
      if (product.referenceName) {
        references.add(product.referenceHouse ? `${product.referenceName} — ${product.referenceHouse}` : product.referenceName);
      }
      if (product.referenceHouse) references.add(product.referenceHouse);
    });
    [...references].sort((left, right) => left.localeCompare(right)).forEach((reference) => {
      const option = document.createElement('option');
      option.value = reference;
      referenceList?.append(option);
    });

    const updateNextState = () => {
      const key = STEP_KEYS[currentStep];
      let hasAnswer = false;
      if (key === 'familiar') hasAnswer = Boolean(answers.reference || answers.direction);
      else if (key === 'budget') hasAnswer = Boolean(answers.size);
      else hasAnswer = Boolean(answers[key]);
      if (nextButton) nextButton.disabled = !hasAnswer;
    };

    const showStep = (index, shouldFocus = true) => {
      currentStep = Math.max(0, Math.min(index, steps.length - 1));
      form.hidden = false;
      results.hidden = true;
      statusbar.hidden = false;
      controls.hidden = false;
      steps.forEach((step, stepIndex) => {
        const active = stepIndex === currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', String(!active));
      });

      const percentage = Math.round(((currentStep + 1) / steps.length) * 100);
      progress?.setAttribute('aria-valuenow', String(currentStep + 1));
      if (progressBar) progressBar.style.width = `${percentage}%`;
      if (progressLabel) progressLabel.textContent = `Question ${currentStep + 1} of ${steps.length}`;
      if (progressValue) progressValue.textContent = `${percentage}%`;
      if (editorialIndex) editorialIndex.textContent = String(currentStep + 1).padStart(2, '0');
      if (read) read.textContent = READ_LINES[currentStep];
      if (backButton) backButton.disabled = currentStep === 0;
      if (nextLabel) nextLabel.textContent = currentStep === steps.length - 1 ? 'Find my scents' : 'Continue';
      if (skipButton) skipButton.textContent = currentStep === steps.length - 1 ? 'Use defaults' : 'Skip';
      if (editorialRestart) editorialRestart.hidden = currentStep === 0;
      updateNextState();

      const legend = steps[currentStep]?.querySelector('legend');
      if (legend && shouldFocus) {
        legend.tabIndex = -1;
        window.requestAnimationFrame(() => legend.focus({ preventScroll: true }));
      }
      if (live) live.textContent = `Question ${currentStep + 1} of ${steps.length}: ${legend?.textContent || ''}`;
    };

    const clearStep = (key) => {
      if (key === 'familiar') {
        answers.reference = '';
        answers.direction = null;
        if (referenceInput) referenceInput.value = '';
        form.querySelectorAll('input[name="direction"]').forEach((input) => { input.checked = false; });
        return;
      }
      if (key === 'budget') {
        answers.size = defaultSize;
        answers.budget = 0;
        const sizeDefault = form.querySelector(`input[name="size"][value="${defaultSize}"]`);
        const budgetDefault = form.querySelector('input[name="budget"][value="0"]');
        if (sizeDefault) sizeDefault.checked = true;
        if (budgetDefault) budgetDefault.checked = true;
        return;
      }
      answers[key] = null;
      form.querySelectorAll(`input[name="${key}"]`).forEach((input) => { input.checked = false; });
    };

    const renderResults = () => {
      const ranked = rankProducts(products, answers);
      const neutralResults = ranked.length > 0 && ranked.every((candidate) => candidate.neutral === true);
      form.hidden = true;
      statusbar.hidden = true;
      controls.hidden = true;
      results.hidden = false;
      if (editorialIndex) editorialIndex.textContent = String(ranked.length).padStart(2, '0');
      if (read) {
        read.textContent = neutralResults
          ? 'Starting points, not fit claims.'
          : ranked.length ? 'Every match has a reason.' : 'Availability stays honest.';
      }
      if (editorialRestart) editorialRestart.hidden = false;

      if (!ranked.length) {
        resultGrid.hidden = true;
        resultGrid.replaceChildren();
        empty.hidden = false;
        if (resultsSummary) resultsSummary.textContent = 'No product passed the live size, price and availability constraints.';
        empty.querySelector('button')?.focus({ preventScroll: true });
        if (live) live.textContent = 'No available match. Edit your size and budget.';
        return;
      }

      empty.hidden = true;
      resultGrid.hidden = false;
      resultGrid.innerHTML = ranked.map((candidate, index) => resultCardMarkup(candidate, index, currency)).join('');
      if (resultsSummary) {
        resultsSummary.textContent = neutralResults
          ? `No scent preference was selected. Showing ${ranked.length} available ${ranked.length === 1 ? 'starting point' : 'starting points'} within your size and ceiling.`
          : `${ranked.length} available ${ranked.length === 1 ? 'scent' : 'scents'}, selected from your answers and ranked by fit.`;
      }
      resultGrid.querySelector('h3 a')?.focus({ preventScroll: true });
      if (live) {
        live.textContent = neutralResults
          ? `${ranked.length} neutral starting points ready. ${ranked.map((candidate) => candidate.product.title).join(', ')}.`
          : `${ranked.length} recommendations ready. ${ranked.map((candidate) => candidate.product.title).join(', ')}.`;
      }
    };

    const resetFinder = () => {
      form.reset();
      Object.assign(answers, {
        presence: null,
        occasion: null,
        weather: null,
        projection: null,
        sweetness: null,
        reference: '',
        direction: null,
        size: defaultSize,
        budget: 0,
      });
      sizeInputs.forEach((input) => { input.checked = input.value === defaultSize; });
      if (referenceInput) referenceInput.value = '';
      resultGrid.replaceChildren();
      showStep(0);
    };

    form.addEventListener('change', (event) => {
      const input = event.target.closest('input[type="radio"]');
      if (!input || !input.checked) return;
      if (input.name === 'budget') answers.budget = Number(input.value) || 0;
      else if (input.name === 'size') answers.size = input.value;
      else if (input.name === 'direction') {
        answers.direction = input.value;
        answers.reference = '';
        if (referenceInput) referenceInput.value = '';
      } else if (Object.prototype.hasOwnProperty.call(answers, input.name)) {
        answers[input.name] = input.value;
      }
      updateNextState();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!nextButton.disabled) nextButton.click();
    });

    referenceInput?.addEventListener('input', () => {
      answers.reference = referenceInput.value.trim();
      if (answers.reference) {
        answers.direction = null;
        form.querySelectorAll('input[name="direction"]').forEach((input) => { input.checked = false; });
      }
      updateNextState();
    });

    root.querySelector('[data-finder-reference-clear]')?.addEventListener('click', () => {
      answers.reference = '';
      if (referenceInput) {
        referenceInput.value = '';
        referenceInput.focus();
      }
      updateNextState();
    });

    nextButton?.addEventListener('click', () => {
      if (nextButton.disabled) return;
      if (currentStep === steps.length - 1) renderResults();
      else showStep(currentStep + 1);
    });

    backButton?.addEventListener('click', () => {
      if (currentStep > 0) showStep(currentStep - 1);
    });

    skipButton?.addEventListener('click', () => {
      clearStep(STEP_KEYS[currentStep]);
      if (currentStep === steps.length - 1) renderResults();
      else showStep(currentStep + 1);
    });

    root.querySelectorAll('[data-finder-restart]').forEach((button) => {
      button.addEventListener('click', resetFinder);
    });

    root.querySelector('[data-finder-edit-constraints]')?.addEventListener('click', () => {
      showStep(steps.length - 1);
    });

    resultGrid?.addEventListener('change', (event) => {
      const select = event.target.closest('[data-finder-result-variant]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const card = select.closest('[data-finder-result-card]');
      const price = card?.querySelector('[data-finder-result-price]');
      if (price && option) price.textContent = formatMoney(Number(option.dataset.price), currency);
      const media = card?.querySelector('.siwa-finder__result-media');
      let image = media?.querySelector('[data-finder-result-image]');
      const placeholder = media?.querySelector('[data-finder-image-placeholder]');
      if (media && option?.dataset.image) {
        if (!image) {
          image = document.createElement('img');
          image.width = 900;
          image.height = 1125;
          image.loading = 'lazy';
          image.dataset.finderResultImage = '';
          media.prepend(image);
        }
        image.src = option.dataset.image;
        image.alt = option.dataset.imageAlt || '';
        placeholder?.remove();
        media.classList.remove('siwa-finder__result-media--empty');
      } else if (media) {
        image?.remove();
        if (!placeholder) {
          const missing = document.createElement('span');
          missing.dataset.finderImagePlaceholder = '';
          missing.textContent = 'Image coming soon';
          media.prepend(missing);
        }
        media.classList.add('siwa-finder__result-media--empty');
      }
      const status = card?.querySelector('[data-finder-add-status]');
      if (status) status.textContent = '';
    });

    resultGrid?.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-finder-add]');
      if (!button || button.disabled) return;
      const card = button.closest('[data-finder-result-card]');
      const select = card?.querySelector('[data-finder-result-variant]');
      const status = card?.querySelector('[data-finder-add-status]');
      const variantId = select?.value;
      if (!variantId) return;

      const buttonLabel = button.querySelector('.siwa-paper__label');
      const originalLabel = buttonLabel?.textContent || button.textContent;
      button.disabled = true;
      if (buttonLabel) buttonLabel.textContent = 'Adding';
      else button.textContent = 'Adding';
      if (status) status.textContent = '';
      try {
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');
        const rootPath = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || 'Could not add this fragrance.');
        await updateCartCount();
        if (buttonLabel) buttonLabel.textContent = 'Added';
        else button.textContent = 'Added';
        if (status) status.textContent = `${payload.product_title || 'The scent'} was added to your bag.`;
        window.setTimeout(() => {
          if (buttonLabel) buttonLabel.textContent = originalLabel;
          else button.textContent = originalLabel;
          button.disabled = false;
        }, 1800);
      } catch (error) {
        if (buttonLabel) buttonLabel.textContent = originalLabel;
        else button.textContent = originalLabel;
        button.disabled = false;
        if (status) status.textContent = error.message || 'Could not add this fragrance.';
      }
    });

    if (!products.length && live) live.textContent = 'The finder catalog is currently unavailable.';
    showStep(0, false);
  };

  document.querySelectorAll('[data-siwa-finder]').forEach(initFinder);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-siwa-finder]').forEach(initFinder);
  });
})();
